import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { employerLogos } from '@/lib/employer-logos';
import styles from './EmployerLogoCarousel.module.css';

/** Original employer marks from the DevClub institutional site. */
export function EmployerLogoCarousel() {
  const id = useId();
  const viewport = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(preference.matches);
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateMotion();
    updateVisibility();
    preference.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? undefined
        : new IntersectionObserver(([entry]) =>
            setVisible(entry.isIntersecting),
          );
    if (observer && viewport.current) observer.observe(viewport.current);
    else setVisible(true);
    return () => {
      preference.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    const node = viewport.current;
    if (
      !node ||
      reducedMotion ||
      !visible ||
      !pageVisible ||
      hovered ||
      focused
    )
      return;
    let frame = 0;
    let previous = 0;
    let position = node.scrollLeft;
    const advance = (time: number) => {
      const loopWidth = list.current?.offsetWidth ?? 0;
      if (previous && loopWidth > 0) {
        // Accumulate fractions so movement stays smooth on low-density screens.
        position =
          (position + Math.min(time - previous, 50) * 0.028) % loopWidth;
        node.scrollLeft = position;
      }
      previous = time;
      frame = requestAnimationFrame(advance);
    };
    frame = requestAnimationFrame(advance);
    return () => cancelAnimationFrame(frame);
  }, [reducedMotion, visible, pageVisible, hovered, focused]);

  return (
    <section className={styles.section} aria-labelledby={`${id}-title`}>
      <div className={styles.heading}>
        <p id={`${id}-title`}>Empresas que já contrataram nossos alunos</p>
      </div>
      <div
        id={`${id}-logos`}
        ref={viewport}
        className={styles.viewport}
        role="group"
        aria-label="Logos das empresas. Role horizontalmente para explorar."
        tabIndex={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <div className={styles.track}>
          {[false, true].map((duplicate) => (
            <ul
              key={String(duplicate)}
              ref={duplicate ? undefined : list}
              className={styles.list}
              aria-hidden={duplicate || undefined}
              aria-label={
                duplicate
                  ? undefined
                  : 'Empresas que contrataram alunos do DevClub'
              }
            >
              {employerLogos.map((logo) => (
                <li
                  key={logo.file}
                  className={styles.logo}
                  style={{ '--logo-scale': logo.scale } as CSSProperties}
                >
                  <img
                    src={`/brand/employers/${logo.file}`}
                    alt={duplicate ? '' : logo.name}
                    width={124}
                    height={48}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>
    </section>
  );
}
